import React, { useState } from 'react';
import axios from 'axios';
import { ShoppingBag, Mail, CheckCircle, ArrowRight, Sparkles } from 'lucide-react';
import { useFeatureFlag } from '../context/FeatureFlagsContext';
import { useNavigate } from 'react-router-dom';

/**
 * Merch page — placeholder layout with email-interest capture.
 *
 * Gated behind the `merch` feature flag (default OFF). When the flag is off
 * and a non-admin lands here, we redirect to home. Admin always sees it so
 * the page can be previewed before the public flip.
 *
 * Real storefront wires in later when product imagery + Stripe / fulfillment
 * (Printful, Shopify, etc.) are ready. Until then this acts as a coming-soon
 * page and an interest-list capture.
 */
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLACEHOLDER_PRODUCTS = [
  { id: 'tee-flame', name: 'Flame Mark Tee', price: '$32', tag: 'COMING SOON', blurb: 'Heavyweight cotton, breathing flame on the chest.' },
  { id: 'hoodie-wordmark', name: 'Enferno Wordmark Hoodie', price: '$68', tag: 'COMING SOON', blurb: 'Cement grey pullover, distressed-print wordmark.' },
  { id: 'cap-low-pro', name: 'Low-Pro Flame Cap', price: '$28', tag: 'COMING SOON', blurb: 'Curved brim, embroidered mark, structured fit.' },
  { id: 'tee-mission', name: 'IronLimb Mission Tee', price: '$32', tag: 'BENEFIT', blurb: '100% of margin goes to IronLimb Foundation.' },
  { id: 'beanie', name: 'Flame Beanie', price: '$24', tag: 'COMING SOON', blurb: 'Cuffed knit, woven label.' },
  { id: 'bottle', name: 'Insulated Water Bottle', price: '$36', tag: 'COMING SOON', blurb: 'Double-walled, 32oz, etched wordmark.' },
];

const Merch = () => {
  const navigate = useNavigate();
  const { enabled: merchEnabled, loading: flagsLoading } = useFeatureFlag('merch');
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle');
  const [errorMsg, setErrorMsg] = useState('');

  // Off + flag has finished loading + user not admin → bounce. We use the
  // server-side flag rather than client-side guard so a public visitor never
  // sees the route at all. Admin bypass: admins can preview by navigating
  // directly; we don't gate them client-side on this page.
  if (!flagsLoading && !merchEnabled) {
    navigate('/');
    return null;
  }

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !/\S+@\S+\.\S+/.test(email)) {
      setErrorMsg('Drop a valid email and we\'ll let you know.');
      setStatus('error');
      return;
    }
    setStatus('submitting');
    setErrorMsg('');
    try {
      // Reuse the sponsorship-application channel for interest capture for
      // now — the founder sees it in the same admin inbox. Will move to a
      // dedicated `merch_interest` collection when the real storefront ships.
      await axios.post(`${API}/sponsorship/apply`, {
        name: 'Merch interest',
        email: email.trim(),
        category: 'other',
        reference_org: 'Merch page interest list',
        story: `Interested in being notified when Enferno merch goes live. Email: ${email.trim()}`,
      });
      setStatus('success');
    } catch {
      setErrorMsg('Something glitched. Try again in a moment.');
      setStatus('error');
    }
  };

  return (
    <div className="bg-transparent text-enf-text-primary min-h-screen pt-20 pb-16">
      <div className="max-w-6xl mx-auto px-6">
        {/* Hero */}
        <div className="mb-12">
          <div className="text-enf-role-info text-xs uppercase tracking-[0.3em] font-semibold mb-2">Merch</div>
          <h1 className="font-display text-6xl md:text-8xl tracking-wide leading-none">Wear the fire.</h1>
          <p className="text-enf-text-secondary mt-3 max-w-2xl text-lg leading-relaxed">
            Built for the gym floor and the long ride home. Heavyweight basics, the flame mark, and a single mission tee where every dollar of margin funds IronLimb Foundation.
          </p>
        </div>

        {/* Placeholder product grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-16" data-testid="merch-product-grid">
          {PLACEHOLDER_PRODUCTS.map((p) => (
            <div
              key={p.id}
              className="bg-enf-surface-deep border border-enf-surface-border rounded-xl overflow-hidden group"
              data-testid={`merch-product-${p.id}`}
            >
              {/* Image placeholder — solid panel with subtle product silhouette to come.
                  Intentionally NOT using the lucide flame here — real product imagery
                  ships when the storefront goes live. */}
              <div className="relative aspect-square bg-gradient-to-br from-zinc-900 via-black to-enf-surface-special flex items-center justify-center overflow-hidden">
                <div className="text-enf-text-muted text-xs uppercase tracking-[0.3em] font-semibold opacity-30 group-hover:opacity-50 transition-opacity duration-500">
                  product render
                </div>
                <div className="absolute top-3 left-3">
                  <span className={`px-2 py-0.5 text-[10px] uppercase tracking-widest font-semibold rounded ${
                    p.tag === 'BENEFIT' ? 'bg-enf-accent text-enf-text-primary' : 'bg-enf-surface-elevated text-enf-text-secondary border border-enf-surface-border'
                  }`}>
                    {p.tag}
                  </span>
                </div>
              </div>
              <div className="p-5">
                <div className="flex items-baseline justify-between gap-3 mb-2">
                  <div className="font-display text-xl tracking-wide leading-tight">{p.name}</div>
                  <div className="text-sm text-enf-text-secondary font-mono flex-shrink-0">{p.price}</div>
                </div>
                <p className="text-xs text-enf-text-muted leading-relaxed">{p.blurb}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Interest capture */}
        <div className="bg-enf-surface-deep border border-enf-accent-fg/30 rounded-xl p-6 md:p-8 mb-12" data-testid="merch-interest-card">
          <div className="flex items-start gap-3 mb-4">
            <Sparkles className="w-5 h-5 text-enf-role-info flex-shrink-0 mt-0.5" />
            <div>
              <div className="text-enf-role-info text-xs uppercase tracking-[0.3em] font-semibold mb-1">First in line</div>
              <h3 className="font-display text-2xl md:text-3xl tracking-wide leading-tight">Get notified when merch drops.</h3>
              <p className="text-sm text-enf-text-secondary mt-2 leading-relaxed">
                We'll email you once when the storefront goes live. No drip campaign, no upsells. Drop your email and forget about us until we deliver.
              </p>
            </div>
          </div>

          {status === 'success' ? (
            <div className="flex items-start gap-3 p-4 bg-adaptive-sage/10 border border-adaptive-sage/30 rounded-md mt-4" data-testid="merch-interest-success">
              <CheckCircle className="w-5 h-5 text-adaptive-sage flex-shrink-0 mt-0.5" />
              <div>
                <div className="font-semibold text-adaptive-sage mb-1">You're on the list.</div>
                <div className="text-xs text-enf-text-secondary">We'll be in touch the day this goes live.</div>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-4 flex flex-col sm:flex-row gap-2">
              <div className="flex-1 relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-enf-text-muted" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="w-full bg-enf-surface-mid border border-enf-surface-border focus:border-enf-role-info rounded-md pl-10 pr-3 py-2.5 text-sm text-enf-text-primary outline-none"
                  data-testid="merch-interest-email"
                  required
                />
              </div>
              <button
                type="submit"
                disabled={status === 'submitting'}
                className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-enf-accent hover:bg-enf-accent-hover rounded-md text-sm font-semibold transition-colors disabled:opacity-50"
                data-testid="merch-interest-submit"
              >
                <ShoppingBag className="w-4 h-4" />
                {status === 'submitting' ? 'Sending…' : 'Notify me'}
                <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </form>
          )}
          {status === 'error' && errorMsg && (
            <p className="mt-3 text-xs text-enf-accent-fg">{errorMsg}</p>
          )}
        </div>

        {/* Mission line */}
        <p className="text-center text-xs text-enf-text-muted leading-relaxed max-w-xl mx-auto">
          A portion of every Enferno product directly supports the IronLimb Foundation's mission. You wear the gear. They help. The heat benefits everyone who steps into it.
        </p>
      </div>
    </div>
  );
};

export default Merch;
